import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth, API } from "../App";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import {
  Briefcase,
  Plus,
  Search,
  Filter,
  MapPin,
  Clock,
  Users,
  Trash2,
  Edit,
  Eye,
  MoreVertical,
  Building2,
  ChevronRight,
  AlertTriangle,
  Loader2,
  Flame
} from "lucide-react";

const ManageJobs = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [filter, setFilter] = useState({
    status: "all",
    industry: "all",
    search: ""
  });

  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    try {
      const response = await fetch(`${API}/jobs/my-jobs`, {
        credentials: "include",
      });

      if (response.ok) {
        const data = await response.json();
        setJobs(data);
      }
    } catch (error) {
      console.error("Failed to fetch jobs:", error);
      toast.error("Failed to load jobs");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteJob = async (jobId) => {
    setDeleting(jobId);
    try {
      const response = await fetch(`${API}/jobs/${jobId}`, {
        method: "DELETE",
        credentials: "include",
      });

      if (response.ok) {
        toast.success("Job deleted successfully");
        setJobs(jobs.filter(j => j.job_id !== jobId));
        setConfirmDelete(null);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to delete job");
      }
    } catch (error) {
      toast.error("Failed to delete job");
    } finally {
      setDeleting(null);
    }
  };

  // Get unique industries for filter
  const industries = [...new Set(jobs.map(j => j.industry).filter(Boolean))];

  // Filter jobs
  const filteredJobs = jobs.filter(job => {
    if (filter.status !== "all" && job.status !== filter.status) return false;
    if (filter.industry !== "all" && job.industry !== filter.industry) return false;
    if (filter.search) {
      const search = filter.search.toLowerCase();
      return job.title?.toLowerCase().includes(search) ||
             job.location?.toLowerCase().includes(search);
    }
    return true;
  });

  if (user?.role !== "recruiter" && user?.role !== "admin") {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-crimson mb-2">Access Denied</h1>
          <p className="text-gray-400">Only recruiters can manage jobs</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-obsidian">
      <Sidebar />
      <main className="lg:ml-64 min-h-screen p-4 sm:p-6 lg:p-8 pt-16 lg:pt-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 sm:mb-8">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold mb-1 sm:mb-2">
              MANAGE <span className="text-crimson">JOBS</span>
            </h1>
            <p className="text-gray-400 text-sm sm:text-base">{jobs.length} total job postings</p>
          </div>
          <Button
            onClick={() => navigate("/jobs/create")}
            className="bg-crimson hover:bg-crimson-hover w-full sm:w-auto"
          >
            <Plus className="w-4 h-4 mr-2" /> Post New Job
          </Button>
        </div>

        {/* Filters */}
        <div className="bg-obsidian-light border border-[#222] p-3 sm:p-4 mb-4 sm:mb-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            {/* Search */}
            <div className="relative sm:col-span-2 lg:col-span-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              <input
                type="text"
                placeholder="Search jobs..."
                value={filter.search}
                onChange={(e) => setFilter({ ...filter, search: e.target.value })}
                className="w-full pl-10 pr-4 py-2 bg-obsidian border border-[#333] text-white text-sm focus:border-crimson outline-none"
              />
            </div>

            {/* Status Filter */}
            <select
              value={filter.status}
              onChange={(e) => setFilter({ ...filter, status: e.target.value })}
              className="px-3 sm:px-4 py-2 bg-obsidian border border-[#333] text-white text-sm focus:border-crimson outline-none"
            >
              <option value="all">All Statuses</option>
              <option value="active">Active</option>
              <option value="closed">Closed</option>
              <option value="draft">Draft</option>
            </select>

            {/* Industry Filter */}
            <select
              value={filter.industry}
              onChange={(e) => setFilter({ ...filter, industry: e.target.value })}
              className="px-3 sm:px-4 py-2 bg-obsidian border border-[#333] text-white text-sm focus:border-crimson outline-none"
            >
              <option value="all">All Industries</option>
              {industries.map(ind => (
                <option key={ind} value={ind}>{ind}</option>
              ))}
            </select>

            {/* Clear Filters */}
            <Button
              onClick={() => setFilter({ status: "all", industry: "all", search: "" })}
              variant="outline"
              className="border-[#333] text-gray-400 text-sm"
            >
              Clear Filters
            </Button>
          </div>
        </div>

        {/* Jobs List */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 text-crimson animate-spin" />
          </div>
        ) : filteredJobs.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-16 h-16 bg-obsidian-light flex items-center justify-center mx-auto mb-4">
              <Briefcase className="w-8 h-8 text-gray-500" />
            </div>
            <h3 className="text-xl font-bold mb-2">No jobs found</h3>
            <p className="text-gray-500 mb-4">
              {jobs.length === 0 ? "You haven't posted any jobs yet" : "No jobs match your filters"}
            </p>
            {jobs.length === 0 && (
              <Button
                onClick={() => navigate("/jobs/create")}
                className="bg-crimson hover:bg-crimson-hover"
              >
                Post Your First Job
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <AnimatePresence>
              {filteredJobs.map((job, index) => (
                <motion.div
                  key={job.job_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, x: -100 }}
                  transition={{ delay: index * 0.03 }}
                  className={`bg-obsidian-light border p-4 sm:p-6 hover:border-crimson/50 transition-all relative ${
                    job.promoted ? "border-crimson/50" : "border-[#222]"
                  }`}
                >
                  {/* Promoted Badge */}
                  {job.promoted && (
                    <div className="absolute top-2 right-2 sm:top-4 sm:right-4 bg-crimson px-2 py-1 flex items-center gap-1">
                      <Flame className="w-3 h-3 text-white" />
                      <span className="text-xs uppercase tracking-wider text-white font-bold">Promoted</span>
                    </div>
                  )}

                  <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                    <div className="flex-1 min-w-0 pr-0 sm:pr-20">
                      <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-2">
                        <h3 className="font-heading text-lg sm:text-xl font-bold break-words">{job.title}</h3>
                        <span className={`px-2 py-1 text-xs uppercase tracking-wider ${
                          job.status === "active" ? "bg-green-500/20 text-green-400" :
                          job.status === "closed" ? "bg-red-500/20 text-red-400" :
                          "bg-gray-500/20 text-gray-400"
                        }`}>
                          {job.status}
                        </span>
                      </div>
                      
                      <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs sm:text-sm text-gray-500 mb-3">
                        {job.industry && (
                          <span className="flex items-center gap-1">
                            <Building2 className="w-4 h-4 flex-shrink-0" />
                            <span className="truncate">{job.industry}</span>
                          </span>
                        )}
                        {job.location && (
                          <span className="flex items-center gap-1">
                            <MapPin className="w-4 h-4 flex-shrink-0" />
                            <span className="truncate">{job.location}</span>
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4 flex-shrink-0" />
                          <span>{new Date(job.created_at).toLocaleDateString()}</span>
                        </span>
                        <span className="flex items-center gap-1">
                          <Users className="w-4 h-4 flex-shrink-0" />
                          <span>{job.applicants?.length || 0} applicants</span>
                        </span>
                      </div>

                      {/* Skills */}
                      {job.skills?.length > 0 && (
                        <div className="flex flex-wrap gap-1 sm:gap-2">
                          {job.skills.slice(0, 3).map((skill, i) => (
                            <span key={i} className="px-2 py-1 bg-[#222] text-gray-400 text-xs">
                              {skill}
                            </span>
                          ))}
                          {job.skills.length > 3 && (
                            <span className="px-2 py-1 text-gray-500 text-xs">
                              +{job.skills.length - 3} more
                            </span>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Button
                        onClick={() => navigate(`/jobs/${job.job_id}`)}
                        variant="outline"
                        size="sm"
                        className="border-[#333] text-gray-400 hover:text-white px-3"
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button
                        onClick={() => navigate(`/jobs/${job.job_id}/edit`)}
                        variant="outline"
                        size="sm"
                        className="border-[#333] text-gray-400 hover:text-white px-3"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        onClick={() => setConfirmDelete(job.job_id)}
                        variant="outline"
                        size="sm"
                        className="border-red-500/30 text-red-400 hover:bg-red-500/10 px-3"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        <AnimatePresence>
          {confirmDelete && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
              onClick={() => setConfirmDelete(null)}
            >
              <motion.div
                initial={{ scale: 0.95 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.95 }}
                className="bg-obsidian border border-[#222] p-6 max-w-md w-full"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-red-500/20 flex items-center justify-center">
                    <AlertTriangle className="w-6 h-6 text-red-400" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-bold">Delete Job?</h3>
                    <p className="text-gray-400 text-sm">This action cannot be undone</p>
                  </div>
                </div>
                
                <p className="text-gray-300 mb-6">
                  Are you sure you want to delete this job posting? All applicants and associated data will be permanently removed.
                </p>

                <div className="flex gap-3">
                  <Button
                    onClick={() => setConfirmDelete(null)}
                    variant="outline"
                    className="flex-1 border-[#333]"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => handleDeleteJob(confirmDelete)}
                    disabled={deleting === confirmDelete}
                    className="flex-1 bg-red-500 hover:bg-red-600 text-white"
                  >
                    {deleting === confirmDelete ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <Trash2 className="w-4 h-4 mr-2" /> Delete
                      </>
                    )}
                  </Button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
};

export default ManageJobs;
